import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { FobChargesService } from 'src/app/services/fob-charges/fob-charges.service';
import { FobChargesModalService } from 'src/app/services/fob-charges-modal/fob-charges-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-md-fob-charges',
  templateUrl: './add-edit-md-fob-charges.component.html',
  styleUrls: ['./add-edit-md-fob-charges.component.scss']
})
export class AddEditMdFobChargesComponent extends DestructorComponent implements OnInit {

  @Output() fobChargeAdded = new EventEmitter<void>();
  @Output() fobChargeUpdated = new EventEmitter<void>();

  fobChargeForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedFobCharge: any = null;

  constructor(
    private fb: FormBuilder,
    private _fobChargesService: FobChargesService,
    private _fobChargesModalService: FobChargesModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.fobChargeForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      charge: [0, [Validators.required, Validators.min(0)]]
    });
  }

  // Getter methods for form controls
  get name() { return this.fobChargeForm.get('name'); }
  get charge() { return this.fobChargeForm.get('charge'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_fob_charge_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceFobCharge = this._fobChargesModalService.getSelectedFobCharge();
        if (serviceFobCharge) {
          this.isEditMode = true;
          this.selectedFobCharge = serviceFobCharge;
          this.populateForm(this.selectedFobCharge);
        } else {
          this.isEditMode = false;
          this.selectedFobCharge = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._fobChargesModalService.clearSelectedFobCharge();
        this.resetForm();
      });
    }
  }

  private populateForm(fobCharge: any): void {
    this.fobChargeForm.patchValue({
      name: fobCharge.name || '',
      charge: fobCharge.charge || 0
    });
  }

  private resetForm(): void {
    this.fobChargeForm.reset({
      name: '',
      charge: 0
    });
    this.isLoading = false;
    this.markFormGroupUntouched(this.fobChargeForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_fob_charge_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.fobChargeForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.fobChargeForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      const apiCall = this.isEditMode
        ? this._fobChargesService.UpdateData(this.selectedFobCharge._id, submitData)
        : this._fobChargesService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('FOB charge operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "FOB charge has been successfully updated!"
                : "FOB charge has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.fobChargeUpdated.emit();
                } else {
                  this.fobChargeAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('FOB charge operation failed:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.fobChargeForm);
    }
  }
}
